import { useCallback, useEffect, useRef, useState } from 'react';
import {
    BOARD_SIZE,
    BOARD_WINDOW,
    beatsPublished,
    buildEntry,
    rankBoard,
} from '../../domain/game/leaderboardEntry';
import { useAuth } from '../account/AuthProvider';

/**
 * The shared board, and this reader's place on it (SPEC-009 PR-9.4).
 *
 * Everything about WHAT a row is and how a board is ordered is
 * `domain/game/leaderboardEntry`; this hook is the four things that cannot be
 * pure — when the board is read, whether a finished run is worth writing, what
 * happens when the network says no, and the fact that all of it is skipped
 * entirely for a signed-out reader.
 *
 * NOTHING HERE RUNS UNTIL SOMEBODY SIGNS IN. That is not an optimisation, it is
 * the feature's shape: the duel is playable, complete and honest signed out
 * (SPEC-007), and a reader who never opens the account sheet must never pay for
 * a Firestore SDK, a network round trip or a failure state belonging to a
 * feature they did not ask for. Every effect below is guarded on
 * `isAuthenticated` first.
 *
 * The failure posture is the same one `AuthProvider` takes and for the same
 * reason (SPEC-009 D-7): a board that cannot be read is a panel with a stated
 * message in it, and the game underneath carries on. This is the first thing in
 * the app that is simply absent offline — the catalogue is same-origin and
 * cached by the service worker, and Firestore is neither.
 */

/** The board's own states. `idle` is the signed-out reader, which is most of them. */
export const BOARD_STATUSES = ['idle', 'loading', 'ready', 'failed'];

/** What happened to the run that just ended. */
export const PUBLISH_STATUSES = ['idle', 'publishing', 'published', 'unbeaten', 'failed'];

const EMPTY_BOARD = { rows: [], standing: null, outsideWindow: false };

function loadClient() {
    return import('../../infrastructure/cloud/leaderboardClient');
}

export default function useLeaderboard({ run }) {
    const { isAuthenticated, user } = useAuth();
    const [board, setBoard] = useState(EMPTY_BOARD);
    const [status, setStatus] = useState('idle');
    const [publishStatus, setPublishStatus] = useState('idle');

    const uid = user?.uid ?? null;
    const displayName = user?.name ?? '';

    /**
     * A generation counter rather than a boolean.
     *
     * Two reads can be in flight at once — the one a sign-in started and the one
     * a finished run started right after it — and the slower of them must not
     * overwrite the fresher board when it lands. Comparing against the count at
     * dispatch is what makes a late reply harmless; a `cancelled` flag alone
     * only handles unmount.
     */
    const generation = useRef(0);

    const refresh = useCallback(async () => {
        if (!uid) {
            setBoard(EMPTY_BOARD);
            setStatus('idle');

            return;
        }

        const dispatched = ++generation.current;

        setStatus('loading');

        try {
            const client = await loadClient();
            // Both reads together: the window is what gets drawn, and the
            // reader's own row is what `beatsPublished` is asked about — and a
            // reader outside the window has no row in it to read.
            const [entries, mine] = await Promise.all([
                client.readTopEntries({ size: BOARD_WINDOW }),
                client.readPublishedEntry(uid),
            ]);

            if (dispatched !== generation.current) {
                return;
            }

            const { rows, standing } = rankBoard(entries, { uid, size: BOARD_SIZE });

            setBoard({
                rows,
                // A reader past the window still has a row and still wants to
                // see it; what they do not get is a number for it (D-9).
                standing: standing ?? (mine ? { rank: null, entry: mine } : null),
                outsideWindow: !standing && Boolean(mine),
            });
            setStatus('ready');
        } catch (error) {
            console.error(error);

            if (dispatched === generation.current) {
                setBoard(EMPTY_BOARD);
                setStatus('failed');
            }
        }
    }, [uid]);

    // Read the board when a reader signs in, and forget it when they sign out.
    // Signing out must leave nothing of somebody else's account on screen.
    useEffect(() => {
        if (!isAuthenticated) {
            generation.current += 1;
            setBoard(EMPTY_BOARD);
            setStatus('idle');
            setPublishStatus('idle');

            return;
        }

        refresh();
    }, [isAuthenticated, refresh]);

    /**
     * Publish the run when it ends.
     *
     * `recorded` holds the VERDICT the last attempt was made from — the same
     * device-board trick `useDuelRun` uses, and for the same reason: a verdict is
     * a fresh object per ending, so it identifies "this run, this time" across
     * the re-renders between the ending and the restart, where a boolean would
     * need clearing and a score would collide with the next run that happened to
     * end on the same number.
     */
    const recorded = useRef(null);

    useEffect(() => {
        if (!isAuthenticated || !uid || run?.status !== 'over' || recorded.current === run.verdict) {
            return;
        }

        recorded.current = run.verdict;

        const entry = buildEntry({ displayName, score: run.score, streak: run.streak });

        // A run worth nothing, or one whose figures the rules would refuse, is
        // simply not published. It is not an error and the reader is not told
        // one: they lost, which the reveal has already said.
        if (!entry) {
            setPublishStatus('idle');

            return;
        }

        let cancelled = false;

        setPublishStatus('publishing');

        loadClient()
            .then(async (client) => {
                const published = await client.readPublishedEntry(uid);

                if (!beatsPublished(entry, published)) {
                    // Not a personal best. The rules would refuse this write —
                    // updates are monotonic (D-2) — so not sending it is the
                    // client agreeing with them rather than guessing.
                    return 'unbeaten';
                }

                await client.publishEntry(uid, entry);

                return 'published';
            })
            .then((outcome) => {
                if (cancelled) {
                    return;
                }

                setPublishStatus(outcome);

                if (outcome === 'published') {
                    refresh();
                }
            })
            .catch((error) => {
                console.error(error);

                if (!cancelled) {
                    setPublishStatus('failed');
                }
            });

        return () => {
            cancelled = true;
        };
        // The run's PARTS rather than the run itself. A caller that rebuilds the
        // run object on every render — which `useDuelRun` does not, but a screen
        // composing one inline easily could — would otherwise re-enter this
        // effect forever, and each pass would be a write. The verdict is still
        // the identity that says "this ending, once"; these dependencies only
        // stop a re-render being mistaken for one.
    }, [isAuthenticated, uid, displayName, run?.status, run?.verdict, run?.score, run?.streak, refresh]);

    // A new run clears what the last one said about itself, so a reader starting
    // again is not still being told where a finished run placed.
    const isAsking = run?.status === 'asking';

    useEffect(() => {
        if (isAsking) {
            setPublishStatus('idle');
        }
    }, [isAsking]);

    return {
        rows: board.rows,
        standing: board.standing,
        outsideWindow: board.outsideWindow,
        status,
        publishStatus,
        refresh,
    };
}
